<template>
  <!-- GROUP: only if parent passes AND at least one visible child -->
  <v-list-group v-if="showGroup" :value="item.value">
    <template #activator="{ props: activatorProps }">
      <v-tooltip :disabled="!rail" location="end" :text="item.title">
        <template #activator="{ props: tooltipProps }">
          <v-list-item
            v-bind="{ ...activatorProps, ...tooltipProps }"
            :prepend-icon="item.props?.prependIcon"
            :title="item.title"
          />
        </template>
      </v-tooltip>
    </template>

    <!-- Forward all listeners to children -->
    <NavListItem
      v-for="child in visibleChildren"
      :key="child.value"
      :item="child"
      v-on="$attrs"
    />
  </v-list-group>

  <!-- SUBHEADER -->
  <v-list-subheader v-else-if="showSubheader">
    {{ item.title }}
  </v-list-subheader>

  <!-- DIVIDER -->
  <v-divider v-else-if="showDivider" />

  <!-- RAIL GROUP PLACEHOLDER (show parent icon/title only in rail mode) -->
  <v-tooltip
    v-else-if="showRailGroupLeaf || showLeaf"
    :disabled="!rail"
    location="end"
    :text="item.title"
  >
    <template #activator="{ props: tooltipProps }">
      <v-list-item
        v-bind="{ ...(item.props || {}), ...tooltipProps }"
        :title="item.title"
        @click="onItemClick(item)"
      >
        <template #prepend>
          <v-icon :color="item.props?.color">
            {{ item.props?.prependIcon }}
          </v-icon>
        </template>
      </v-list-item>
    </template>
  </v-tooltip>
</template>

<script setup>
  import { usePermissionEvaluator } from '#xapp-core/foundation'
  import { computed } from 'vue'

  defineOptions({ inheritAttrs: false })

  const props = defineProps({
    item: { required: true, type: Object },
    rail: { default: false, type: Boolean },
  })
  // eslint-disable-next-line vue/valid-define-emits
  const emit = defineEmits()

  const { hasPermission } = usePermissionEvaluator()

  const visibleChildren = computed(() =>
    (props.item.children ?? []).filter((element) => hasPermission(element))
  )

  const showGroup = computed(() => {
    const hasKids =
      Array.isArray(props.item.children) && props.item.children.length > 0
    return (
      !props.rail &&
      hasKids &&
      hasPermission(props.item) &&
      visibleChildren.value.length > 0
    )
  })
  const showRailGroupLeaf = computed(() => {
    const hasKids =
      Array.isArray(props.item.children) && props.item.children.length > 0
    return (
      props.rail &&
      hasKids &&
      hasPermission(props.item) &&
      visibleChildren.value.length > 0
    )
  })
  const showDivider = computed(
    () => props.item.type === 'divider' && hasPermission(props.item)
  )
  const showSubheader = computed(
    () => props.item.type === 'subheader' && hasPermission(props.item)
  )
  const showLeaf = computed(() => {
    const isLeaf = !props.item.children || props.item.children.length === 0
    const notSpecial =
      props.item.type !== 'divider' && props.item.type !== 'subheader'
    return isLeaf && notSpecial && hasPermission(props.item)
  })

  function onItemClick(node) {
    const ev = node.emit ?? node.emits ?? node.event ?? node.events
    const names = Array.isArray(ev) ? ev : ev ? [ev] : []
    for (const name of names) emit(name, node)
    emit('item-click', node)
  }
</script>
